import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Modal, Table as AntTable, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Table from "@/components/Table";
import Button from "@components/Button";
import CompanyList from "@/components/List/company-list";
import { ICompany } from "@/types";

import {
  useFetchAllJobFunctionsQuery,
  useFetchAllCompanyFunctionsMappingQuery,
  useFetchCompaniesQuery,
  useDownloadCompanyFunctionMappingCSVMutation,
  useUploadCompanyFunctionMappingCSVMutation,
  ErrorServices,
  useDownloadCompanyFunctionMappingAISuggestionsCSVMutation,
} from "@/services";

import "./companyFunctionsMapTable.less";
// import { handleCsvUpload } from "@/utils/uploadCsv";
import Upload from "@/components/Upload";

const { Option } = Select;

type SubRow = {
  id: string;
  companySubJobFunctionName: string;
  subJobFunctionId?: string;
};

type RowData = {
  id: string;
  companyJobFunctionName: string;
  jobFunctionId?: string;
  subRows: SubRow[];
};

const CompanyFunctionsMapTable: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // ---------------- COMPANY LIST ----------------

  const { data: companiesData, isLoading: isFetchingCompanies } =
    useFetchCompaniesQuery(1);

  const companies: ICompany[] = companiesData?.data || [];

  // ---------------- APIs ----------------

  const { data: jobFunctionsRes, isLoading: jfLoading } =
    useFetchAllJobFunctionsQuery({});

  const {
    data: mappingRes,
    isLoading: mappingLoading,
    refetch,
  } = useFetchAllCompanyFunctionsMappingQuery(
    {
      company_id: companyId!,
      page,
      page_size: pageSize,
    },
    { skip: !companyId }
  );

  const jobFunctions = jobFunctionsRes?.data || [];
  const pagination = mappingRes?.pagination;

  const [downloadCurrentMappingCSVApi, { isLoading: downloading }] =
    useDownloadCompanyFunctionMappingCSVMutation();

  const [uploadMappingCSVApi, { isLoading: uploading }] =
    useUploadCompanyFunctionMappingCSVMutation();

  const [downloadAiMappingCSVApi, { isLoading: downloadingAi }] =
    useDownloadCompanyFunctionMappingAISuggestionsCSVMutation();

  // ---------------- SUB MAP ----------------

  const subMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    jobFunctions.forEach((jf: any) => {
      map[String(jf.id)] = jf.job_sub_functions || [];
    });
    return map;
  }, [jobFunctions]);

  // ---------------- FLATTEN ----------------

  const mappedData: RowData[] = useMemo(() => {
    const list = mappingRes?.data || [];

    return list.map((item: any) => {
      const jobFunctionId = item.ta_job_function_id
        ? String(item.ta_job_function_id)
        : undefined;

      const subMappings = item.sub_job_function_mappings || [];

      return {
        id: String(item.id),
        companyJobFunctionName: item.company_job_function_name,
        jobFunctionId,
        subRows: subMappings.map((sub: any) => ({
          id: String(sub.id),
          companySubJobFunctionName: sub.company_sub_job_function_name,
          subJobFunctionId: sub.ta_sub_job_function_id
            ? String(sub.ta_sub_job_function_id)
            : undefined,
        })),
      };
    });
  }, [mappingRes]);

  // ---------------- STATE ----------------

  const [data, setData] = useState<RowData[]>([]);

  useEffect(() => {
    if (mappedData.length) {
      setData(mappedData);
    } else if (companyId) {
      setData([]);
    }
  }, [mappedData, companyId]);

  // ---------------- UNMAPPED ----------------

  // const hasUnmapped = useMemo(() => {
  //   return data.some(
  //     row =>
  //       !row.jobFunctionId || row.subRows.some(sub => !sub.subJobFunctionId)
  //   );
  // }, [data]);

  // ---------------- HANDLERS ----------------

  const handleJobChange = (rowId: string, jobFunctionId: string) => {
    setData(prev =>
      prev.map(row =>
        row.id === rowId
          ? {
              ...row,
              jobFunctionId,
              subRows: row.subRows.map(sub => ({
                ...sub,
                subJobFunctionId: undefined,
              })),
            }
          : row
      )
    );
  };

  const handleSubChange = (
    rowId: string,
    subId: string,
    subJobFunctionId: string
  ) => {
    setData(prev =>
      prev.map(row =>
        row.id === rowId
          ? {
              ...row,
              subRows: row.subRows.map(sub =>
                sub.id === subId ? { ...sub, subJobFunctionId } : sub
              ),
            }
          : row
      )
    );
  };

  // ---------------- AI ----------------

  // const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ---------------- CSV ----------------

  const downloadCurrentMappingCSV = async () => {
    if (!companyId || downloading) return;

    message.loading({
      content: "Downloading Current Mappings...",
      key: "download",
    });

    downloadCurrentMappingCSVApi({ company_id: companyId })
      .unwrap()
      .then(() => {
        message.success({
          content: "Download Started",
          key: "download",
        });
      })
      .catch((err: any) => {
        message.destroy("download");
        ErrorServices(err);
      });
  };

  const downloadAiMappings = async () => {
    if (!companyId || downloadingAi) return;

    message.loading({
      content: "Downloading AI Mappings...",
      key: "ai-download",
    });

    downloadAiMappingCSVApi({ company_id: companyId })
      .unwrap()
      .then(() => {
        message.success({
          content: "Download Started",
          key: "ai-download", // ✅ FIXED
        });
      })
      .catch((err: any) => {
        message.destroy("ai-download");
        ErrorServices(err);
      });
  };

  const handleUpload = async () => {
    if (!companyId || !uploadFile) return;

    try {
      const formData = new FormData();
      formData.append("attachment", uploadFile, uploadFile.name);

      const res = await uploadMappingCSVApi({
        company_id: companyId,
        formData, // ⚠️ match your API (NOT body unless defined)
      }).unwrap();

      message.success(res?.message || "CSV uploaded successfully");

      refetch(); // keep this (important)
    } catch (error: any) {
      ErrorServices(error);
    }
  };

  // const handleUploadClick = () => {
  //   if (uploading) return;

  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = "";
  //     fileInputRef.current.click();
  //   }
  // };

  // const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!companyId || uploading) return;

  //   const file = e.target.files?.[0];

  //   // ❌ No file selected
  //   if (!file) {
  //     message.error("No file selected");
  //     return;
  //   }

  //   // ❌ Empty file (0 bytes)
  //   if (file.size === 0) {
  //     message.error("File is empty. Please upload a valid CSV.");
  //     return;
  //   }

  //   // ✅ Read file content
  //   const reader = new FileReader();

  //   reader.onload = event => {
  //     const text = event.target?.result as string;

  //     // ❌ No content or only headers
  //     const rows = text
  //       .split(/\r?\n/)
  //       .map(row => row.trim())
  //       .filter(row => row.length > 0);

  //     if (rows.length <= 1) {
  //       message.error("CSV has no data rows");
  //       return;
  //     }

  //     // ✅ Only now proceed with upload
  //     handleCsvUpload(
  //       e,
  //       formData =>
  //         uploadMappingCSVApi({
  //           company_id: companyId,
  //           formData,
  //         }),
  //       {
  //         loadingMessage: "Uploading mappings...",
  //         successMessage: "CSV uploaded successfully",
  //         onSuccess: () => refetch(),
  //       }
  //     );
  //   };

  //   reader.readAsText(file);
  // };

  // ---------------- COLUMNS ----------------

  const columns = [
    {
      title: "Company Job Function",
      dataIndex: "companyJobFunctionName",
      width: 330,
    },
    {
      title: "TA Job Function",
      width: 330,
      render: (_: any, record: RowData) => (
        <Select
          disabled
          style={{ width: "100%" }}
          loading={jfLoading}
          value={record.jobFunctionId ?? undefined}
          onChange={value => handleJobChange(record.id, value)}
        >
          {jobFunctions.map((jf: any) => (
            <Option key={jf.id} value={String(jf.id)}>
              <div className="truncate-text">{jf.name}</div>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Company Sub Job Function",
      width: 330,
      render: (_: any, record: RowData) => (
        <div>
          {record.subRows.map(sub => (
            <div
              className="py-3 border-b"
              key={sub.id}
              style={{ marginBottom: 8 }}
            >
              {sub.companySubJobFunctionName}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "TA Job Sub-Function",
      width: 330,
      render: (_: any, record: RowData) => {
        const subs = record.jobFunctionId
          ? subMap[record.jobFunctionId] || []
          : [];

        return (
          <div>
            {record.subRows.map(sub => (
              <div className="py-2" key={sub.id}>
                <Select
                  style={{ width: "100%", marginBottom: 8 }}
                  value={sub.subJobFunctionId ?? undefined}
                  // disabled={!record.jobFunctionId}
                  disabled
                  onChange={value => handleSubChange(record.id, sub.id, value)}
                >
                  {subs.map((s: any) => (
                    <Option key={s.id} value={String(s.id)}>
                      <div className="truncate-text">{s.name}</div>
                    </Option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  // ---------------- COMPANY SELECTION SCREEN ----------------

  if (!companyId) {
    return (
      <div className="ApplicationSelector w-full px-12">
        <div className="flex justify-between items-center mt-6 px-4">
          <h2 className="font-bold text-xl text-themeBlue text-left">
            Select the Company
          </h2>
        </div>

        {isFetchingCompanies ? (
          <div
            style={{ height: "650px" }}
            className="flex justify-center items-center"
          >
            <LoadingOutlined className="spinner" style={{ fontSize: "80px" }} />
          </div>
        ) : (
          <main className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full">
              <CompanyList
                companiesList={companies}
                onSelectCompany={setSelectedCompany}
                selectedCompany={selectedCompany}
              />

              {selectedCompany && (
                <div className="mt-8 flex gap-2 sticky bottom-2">
                  <button
                    onClick={() => {
                      setCompanyId(Number(selectedCompany));
                      setPage(1);
                    }}
                    className="flex-1 bg-themeBlue text-white px-5 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {
                      companies.find(
                        (item: ICompany) => String(item.id) === selectedCompany
                      )?.name
                    }
                  </button>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <>
      {isUploadModal && (
        <Upload
          title="Upload Mappings"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}
      {/* <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        hidden
        onChange={handleFileUpload}
      /> */}

      <div className="appTitle text-base font-[500] mx-5">
        Company Functions Mapping
      </div>

      <div className="jobCreationBar sticky top-0 z-10 bg-white px-14 py-6 rounded-lg flex justify-between items-center my-7">
        <div className="title text-themeBlue font-[500] text-3xl">
          Company Functions Mapping
        </div>

        <div className="flex gap-2">
          <Button
            variant="download"
            onClick={downloadCurrentMappingCSV}
            disabled={downloading}
          >
            Download Current Mappings
          </Button>

          <Button
            variant="upload"
            onClick={() => setIsUploadModal(true)}
            disabled={uploading}
          >
            Upload Mappings
          </Button>

          {/* <Button
            variant="upload"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {" "}
            Upload Mappings
          </Button> */}

          <Button
            variant="download"
            onClick={downloadAiMappings}
            disabled={downloadingAi}
          >
            Download AI Mappings
          </Button>
        </div>
      </div>

      <div className="jobCreationListing bg-white px-14 py-6 rounded-lg">
        <Table
          columns={columns}
          data={data}
          pagination
          page={page}
          count={pagination?.count}
          onChangePage={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </>
  );
};

export default CompanyFunctionsMapTable;
